// Fleet Reports — Driving Behavior section (P2, 2026-07-16).
//
// RULE-DRIVEN and DYNAMIC: the rows of the by-rule tables are whatever rules are
// active on the tenant's fleet service — one row per rule, with the label and
// severity taken straight from the payload (never hardcoded). Add a rule in the
// platform and it appears here automatically.
//
// The number here can differ from the platform's Alarms page by design (it is
// reconstructed from telemetry, and the platform has its own debounce/dedupe and
// deleted-rule history). The disclaimer + skippedRules + partialWindow flags say so.
import React from "react";
import { Card, Grid } from "@mui/material";
import FleetBar from "./charts/FleetBar";
import StatTile from "./StatTile";
import { RAG, ragForSeries, ragBg } from "./ragColors";

const SEVERITY_COLOR = { CRITICAL: "#bf3535", MAJOR: "#844204", MINOR: "#fe9f1b", WARNING: "#3399ff" };

const sectionTitle = (text) => (
  <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
    {text}
  </p>
);

const SeverityChip = ({ severity }) => {
  // rule.severity is free text (no enum on the model), so normalise the casing
  // before keying the colour — a "Major"/"major" would otherwise fall back to
  // grey. Cumulocity severities are canonically upper-case, so display upper too.
  const key = String(severity || "").trim().toUpperCase();
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "11px",
        fontWeight: "bold",
        color: "#fff",
        background: SEVERITY_COLOR[key] || "#7f8c8d",
        borderRadius: "4px",
        padding: "1px 6px",
      }}
    >
      {key || "-"}
    </span>
  );
};

export default function BehaviorSection({ report }) {
  const m = (report && report.metrics && report.metrics.behavior) || null;
  const meta = (report && report.meta) || {};
  const vehicles = (report && report.vehicles) || [];

  const weekRows = (m && m.perVehicleWeek) || [];
  const ragWeek = React.useMemo(() => ragForSeries(weekRows, false), [weekRows]);
  const barData = React.useMemo(
    () => weekRows.map((r) => ({ key: r.sensorId, label: r.name || r.sensorId, value: r.value, color: ragWeek(r.value) })),
    [weekRows, ragWeek]
  );

  const byType = (m && m.fleetWeekByType) || [];
  const ragType = React.useMemo(() => ragForSeries(byType.map((t) => ({ value: t.count })), false), [byType]);

  // Pivot the flat perVehicleWeekByType (one row per rule × vehicle) into a
  // rule-rows × vehicle-cols matrix. Keyed on ruleId so a rename can't split a row.
  const matrix = React.useMemo(() => {
    const rows = new Map();
    for (const r of (m && m.perVehicleWeekByType) || []) {
      if (!rows.has(r.ruleId)) rows.set(r.ruleId, { ruleId: r.ruleId, type: r.type, severity: r.severity, byVehicle: {}, total: 0 });
      rows.get(r.ruleId).byVehicle[r.sensorId] = r.count;
      rows.get(r.ruleId).total += r.count;
    }
    return [...rows.values()].sort((a, b) => b.total - a.total);
  }, [m]);

  if (!m) return null;

  const activeRules = meta.activeRules || [];
  const skipped = meta.skippedRules || [];
  const partial = activeRules.filter((r) => r.partialWindow);

  return (
    <div>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={4} md={3}>
          <StatTile label="Fleet Behavior" value={m.fleetWeek} unit="events" better="lower" decimals={0} />
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle("Per vehicle (events)")}
            <FleetBar data={barData} unit="events" height="240px" valueFormat="#" />
          </Card>
        </Grid>
      </Grid>

      {/* Fleet total by rule */}
      <Card style={{ padding: "16px", marginBottom: "20px" }}>
        {sectionTitle("Fleet total by rule")}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#e2e2e2" }}>
                <th style={{ textAlign: "left", padding: "10px" }}>Severity</th>
                <th style={{ textAlign: "left", padding: "10px" }}>Rule</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Events</th>
              </tr>
            </thead>
            <tbody>
              {byType.map((t) => {
                const c = ragType(t.count);
                return (
                  <tr key={t.ruleId || t.type} style={{ borderBottom: "1px solid #eeeeee" }}>
                    <td style={{ padding: "10px" }}><SeverityChip severity={t.severity} /></td>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{t.type}</td>
                    <td style={{ padding: "10px", textAlign: "right", backgroundColor: ragBg(c) }}>{t.count}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
                <td style={{ padding: "10px" }} />
                <td style={{ padding: "10px" }}>Fleet total</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{m.fleetWeek}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-vehicle by rule matrix */}
      <Card style={{ padding: "16px" }}>
        {sectionTitle("Per-vehicle total by rule")}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#e2e2e2" }}>
                <th style={{ textAlign: "left", padding: "10px" }}>Rule</th>
                {vehicles.map((v) => (
                  <th key={v.sensorId} style={{ textAlign: "right", padding: "10px" }}>{v.name || v.sensorId}</th>
                ))}
                <th style={{ textAlign: "right", padding: "10px" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => {
                const rag = ragForSeries(vehicles.map((v) => ({ value: row.byVehicle[v.sensorId] || 0 })), false);
                return (
                  <tr key={row.ruleId} style={{ borderBottom: "1px solid #eeeeee" }}>
                    <td style={{ padding: "10px" }}>
                      <SeverityChip severity={row.severity} /> <span style={{ fontWeight: "bold" }}>{row.type}</span>
                    </td>
                    {vehicles.map((v) => {
                      const val = row.byVehicle[v.sensorId] || 0;
                      return (
                        <td key={v.sensorId} style={{ padding: "10px", textAlign: "right", backgroundColor: ragBg(rag(val)) }}>
                          {val}
                        </td>
                      );
                    })}
                    <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold" }}>{row.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p style={{ fontSize: "13px", color: "grey", marginTop: "12px", marginBottom: 0 }}>
          <b>Telemetry-derived:</b> these counts are reconstructed from vehicle telemetry against the fleet's
          active rules, and may differ slightly from the platform's Alarms history.
          {partial.length > 0 ? (
            <>
              {" "}
              <b style={{ color: RAG.warn }}>Note:</b> {partial.map((r) => r.name).join(", ")}{" "}
              {partial.length === 1 ? "was" : "were"} added or changed inside this window, so the count may not cover the full period.
            </>
          ) : null}
          {skipped.length > 0 ? (
            <>
              {" "}
              Not evaluated: {skipped.map((r) => `${r.name} (${r.reason})`).join("; ")}.
            </>
          ) : null}
        </p>
      </Card>
    </div>
  );
}
